import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { announceQueued, isQueued } from "@/lib/queuedStart";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { apiErrorDetail } from "@/lib/api-error";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useScoringStore, type ScoringMode } from "@/stores/scoring-store";
import { trackEvent } from "@/lib/engagement";
import type { ScoringStartResponse } from "@/types/paper";

interface ScoringOptions {
  paperIds?: string[];
  rescore?: boolean;
  interactive?: boolean;
  mode?: ScoringMode;
}

/** Told once whether POST /scoring/start was accepted (the SSE stream then runs on regardless). */
interface AcceptanceHooks {
  onAccepted?: () => void;
  onRejected?: (err: unknown) => void;
}

export function useScoring(conceptId: string) {
  const queryClient = useQueryClient();

  const startScoring = useCallback(
    async (options: ScoringOptions = {}, hooks: AcceptanceHooks = {}) => {
      let accepted = false;
      trackEvent("feature_use", { feature: "scoring", action: "started", mode: options.mode ?? "standard" });
      const { startScoring, updateProgress, completeScoring, failScoring } =
        useScoringStore.getState();

      const mode = options.mode ?? "standard";

      try {
        // Immediately show scoring state for instant UI feedback — unless
        // this tab is already showing a run (a queued reply mustn't wipe it).
        const showedPending = !useScoringStore.getState().isScoring;
        if (showedPending) startScoring("pending", mode, options.paperIds?.length ?? 0);

        // POST to start scoring job
        const body: Record<string, unknown> = { mode };
        if (options.paperIds) body.paper_ids = options.paperIds;
        if (options.rescore) body.rescore = true;
        if (options.interactive) body.interactive = true;

        const reply = await apiClient
          .post(`api/concepts/${conceptId}/scoring/start`, { json: body })
          .json<ScoringStartResponse | unknown>();
        // Already running (or a background batch is): the server holds this
        // start and runs it next — accepted, just not yet.
        if (isQueued(reply)) {
          if (showedPending) useScoringStore.getState().resetScoring();
          accepted = true;
          hooks.onAccepted?.();
          announceQueued(queryClient, conceptId, "Scoring", reply);
          return;
        }
        const res = reply as ScoringStartResponse;

        // Update with real job ID and total from server
        startScoring(res.job_id, mode, res.total_papers);
        accepted = true;
        hooks.onAccepted?.();

        // Backend capped the job to the remaining plan quota — say so up
        // front (the job itself completes normally). Long duration: this is
        // the user's only signal that not everything they selected scored.
        if (res.quota_skipped > 0) {
          toast.warning("Plan scoring limit reached", {
            description:
              `Scoring ${res.total_papers} of ` +
              `${res.total_papers + res.quota_skipped} papers — ` +
              `${res.quota_skipped} skipped. Upgrade to score more per concept.`,
            duration: 10_000,
          });
        }

        // Open SSE stream
        const baseUrl =
          import.meta.env.VITE_API_URL ?? "http://localhost:8000";
        const token = await getAuthToken();
        const streamUrl = `${baseUrl}/api/concepts/${conceptId}/scoring/${res.job_id}/stream`;

        const controller = new AbortController();

        let receivedTerminal = false;

        for await (const event of streamSSE(
          streamUrl,
          { Authorization: `Bearer ${token}` },
          controller.signal,
        )) {
          if (event.event === "preparing") {
            const data = JSON.parse(event.data);
            updateProgress({
              percent: 2,
              statusMessage: data.message || "Preparing...",
            });
          } else if (event.event === "scoring_started") {
            const data = JSON.parse(event.data);
            updateProgress({
              total: data.total_papers,
              totalBatches: data.total_batches,
              percent: 5,
              statusMessage: `Scoring ${data.total_papers} papers...`,
            });
          } else if (event.event === "batch_started") {
            // All batches fire concurrently — show that scoring is active.
            // Use scoring_started's total: batches × this batch's size
            // overcounts whenever the last batch is partial (351 → "360").
            const data = JSON.parse(event.data);
            const { scored, total } = useScoringStore.getState().progress;
            if (scored === 0) {
              updateProgress({
                statusMessage: `Scoring ${total || data.total_batches * data.papers_in_batch} papers...`,
              });
            }
          } else if (event.event === "batch_complete") {
            const data = JSON.parse(event.data);
            const percent = 5 + Math.round((data.scored / data.total) * 90);
            updateProgress({
              scored: data.scored,
              total: data.total,
              currentBatch: data.batch,
              percent,
              statusMessage: `Scored ${data.scored} of ${data.total} papers`,
            });
          } else if (event.event === "normalizing") {
            const data = JSON.parse(event.data);
            updateProgress({
              percent: 95,
              statusMessage: `Normalizing ${data.total} scores...`,
            });
          } else if (event.event === "scoring_batch_submitted") {
            // Tier 4 hybrid job: head scored interactively, tail submitted
            // as a Message Batch. Terminal for THIS job — the durable
            // scoring_batch_jobs row (polled via useScoringBatchStatus)
            // owns the rest.
            const data = JSON.parse(event.data);
            useScoringStore.getState().startBackgroundBatch(data.tail_total);
            toast.info("Scoring continues in background", {
              description:
                `${data.head_scored} papers scored now — ` +
                `${data.tail_total} more landing in the background.`,
            });
            receivedTerminal = true;
            await queryClient.invalidateQueries({
              queryKey: ["papers", conceptId],
            });
            await queryClient.invalidateQueries({
              queryKey: ["scoring-batch", conceptId],
            });
            return;
          } else if (event.event === "complete") {
            const data = JSON.parse(event.data);
            const msg = data.message || `Scored ${data.total_scored} papers, normalized ${data.normalized}`;
            completeScoring(msg);
            toast.success("Scoring complete", { description: msg });
            receivedTerminal = true;
            await queryClient.invalidateQueries({
              queryKey: ["papers", conceptId],
            });
            // Separate key from ["papers"]: without this the "Score N
            // unscored" nudge keeps a count read mid-run.
            await queryClient.invalidateQueries({ queryKey: ["papers-count", conceptId] });
            return;
          } else if (event.event === "error") {
            const data = JSON.parse(event.data);
            const errMsg = data.error || "Scoring failed";
            failScoring(errMsg);
            toast.error("Scoring failed", { description: errMsg });
            receivedTerminal = true;
            return;
          } else if (event.event === "cancelled") {
            // User-initiated cancel is not a failure. failScoring() here
            // left a permanent error pill AND locked the curate ActionBar
            // (its busy gate saw the stale error) — reset to idle instead;
            // the toast is the confirmation.
            useScoringStore.getState().resetScoring();
            toast.info("Scoring cancelled");
            receivedTerminal = true;
            return;
          }
        }

        // Stream closed without terminal event — refresh table as fallback
        if (!receivedTerminal) {
          completeScoring("Scoring finished");
          toast.success("Scoring complete");
          await queryClient.invalidateQueries({
            queryKey: ["papers", conceptId],
          });
          await queryClient.invalidateQueries({ queryKey: ["papers-count", conceptId] });
        }
      } catch (err) {
        const { failScoring } = useScoringStore.getState();
        // Surface the server's detail (quota copy) instead of ky's generic
        // "Request failed with status code N" — the rescore paths now rely
        // on the backend gate, so its 403 copy must reach the user.
        const msg = await apiErrorDetail(err, "Scoring failed");
        failScoring(msg);
        toast.error("Scoring error", { description: msg });
        if (!accepted) hooks.onRejected?.(err);
      }
    },
    [conceptId, queryClient],
  );

  /** startScoring, but resolves once the server accepts the job (2xx) and rejects with the POST's
   *  error (e.g. 409: a scoring run is already going) — for callers that must know it started.
   *  The stream keeps running into the scoring store after this settles. */
  const requestScoring = useCallback(
    (options: ScoringOptions = {}) =>
      new Promise<void>((resolve, reject) => {
        void startScoring(options, { onAccepted: resolve, onRejected: reject });
      }),
    [startScoring],
  );

  const cancelScoring = useCallback(async () => {
    const { jobId } = useScoringStore.getState();
    if (!jobId) return;

    try {
      await apiClient.post(
        `api/concepts/${conceptId}/scoring/${jobId}/cancel`,
      );
      // Optimistic reset — pill drops and busy gates release immediately;
      // the SSE "cancelled" terminal (seconds later) toasts confirmation
      // and resets again (no-op). Not failScoring: cancel is not an error.
      useScoringStore.getState().resetScoring();
    } catch {
      // Best-effort cancel
    }
  }, [conceptId]);

  return { startScoring, requestScoring, cancelScoring };
}
